import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  type MotionStyle
} from "framer-motion";
import { Link } from "react-router-dom";
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  MouseEvent,
  ReactNode
} from "react";

type Variant = "primary" | "secondary" | "ghost";
type Size = "md" | "lg";

interface CommonProps {
  children: ReactNode;
  className?: string;
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
}

type LinkProps = CommonProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    href: string;
    to?: undefined;
  };

type RouteLinkProps = CommonProps & {
  to: string;
  href?: undefined;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">;

type ButtonProps = CommonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined;
    to?: undefined;
  };

const variantMap: Record<Variant, string> = {
  primary:
    "bg-teal-700 text-stone-50 shadow-[0_20px_44px_-26px_rgba(21,93,95,0.8)] hover:bg-teal-800",
  secondary:
    "border border-teal-900/10 bg-white/70 text-stone-900 shadow-[0_16px_32px_-24px_rgba(27,53,52,0.35)] hover:bg-white",
  ghost: "border border-white/70 bg-white/50 text-stone-900 hover:bg-white/70"
};

const sizeMap: Record<Size, string> = {
  md: "px-5 py-3 text-sm md:text-base",
  lg: "px-6 py-3.5 text-base"
};

function useMagneticStyle() {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 140, damping: 18, mass: 0.6 });
  const springY = useSpring(y, { stiffness: 140, damping: 18, mass: 0.6 });
  const rotate = useTransform(springX, [-8, 8], [-2, 2]);

  const style: MotionStyle = {
    x: springX,
    y: springY,
    rotate
  };

  const handleMove = (event: MouseEvent<HTMLElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const offsetX = (event.clientX - bounds.left - bounds.width / 2) / 10;
    const offsetY = (event.clientY - bounds.top - bounds.height / 2) / 10;

    x.set(offsetX);
    y.set(offsetY);
  };

  const handleLeave = () => {
    x.set(0);
    y.set(0);
  };

  return { style, handleMove, handleLeave };
}

export function MagneticButton(props: LinkProps | RouteLinkProps | ButtonProps) {
  const { style, handleMove, handleLeave } = useMagneticStyle();
  const {
    children,
    className,
    variant = "primary",
    size = "lg",
    fullWidth = false
  } = props;

  const sharedClassName = [
    "inline-flex items-center justify-center rounded-full font-medium tracking-tight transition-[background-color,box-shadow,color,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] active:translate-y-px active:scale-[0.98]",
    variantMap[variant],
    sizeMap[size],
    fullWidth ? "w-full" : "",
    className ?? ""
  ]
    .join(" ")
    .trim();

  if ("to" in props && props.to) {
    const {
      to,
      variant: _variant,
      size: _size,
      fullWidth: _fullWidth,
      className: _className,
      children: _children,
      ...routeProps
    } = props as RouteLinkProps;

    return (
      <motion.div
        style={style}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        whileTap={{ scale: 0.98 }}
        className={fullWidth ? "w-full" : "inline-flex"}
      >
        <Link {...routeProps} to={to} className={sharedClassName}>
          {children}
        </Link>
      </motion.div>
    );
  }

  if ("href" in props && props.href) {
    const {
      href,
      variant: _variant,
      size: _size,
      fullWidth: _fullWidth,
      className: _className,
      children: _children,
      ...linkProps
    } = props as LinkProps;

    return (
      <motion.div
        style={style}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        whileTap={{ scale: 0.98 }}
        className={fullWidth ? "w-full" : "inline-flex"}
      >
        <a {...linkProps} href={href} className={sharedClassName}>
          {children}
        </a>
      </motion.div>
    );
  }

  const {
    variant: _variant,
    size: _size,
    fullWidth: _fullWidth,
    className: _className,
    children: _children,
    type,
    ...buttonProps
  } = props as ButtonProps;
  const buttonType: "button" | "submit" | "reset" =
    type === "submit" || type === "reset" || type === "button" ? type : "button";

  return (
    <motion.div
      style={style}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      whileTap={{ scale: 0.98 }}
      className={fullWidth ? "w-full" : "inline-flex"}
    >
      <button {...buttonProps} type={buttonType} className={sharedClassName}>
        {children}
      </button>
    </motion.div>
  );
}
